/**
 * QuoteAttachments — file sharing inside a quote request. Either party
 * attaches plans, photos or documents (PDF/DOC/DOCX/photos/videos); the other
 * party is notified and views via signed URLs. Uploader can remove their own.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { AttachFile, DeleteOutline, InsertDriveFile } from '@mui/icons-material';
import api from '../../services/api';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const QuoteAttachments = ({ quoteRequestId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const loadFiles = useCallback(async () => {
    if (!quoteRequestId) return;
    setLoading(true);
    try {
      const response = await api.get(`/quote-requests/${quoteRequestId}/files`);
      setFiles(response.data.data?.files || []);
    } catch (err) {
      console.error('Load quote files error:', err);
    } finally {
      setLoading(false);
    }
  }, [quoteRequestId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleUpload = async (event) => {
    const selected = Array.from(event.target.files || []).slice(0, 5);
    event.target.value = '';
    if (selected.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      selected.forEach((file) => formData.append('files', file));
      await api.post(`/quote-requests/${quoteRequestId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      loadFiles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to attach files.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/quote-requests/${quoteRequestId}/files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove file.');
    }
  };

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">Attachments</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={20} sx={{ display: 'block', my: 1 }} />
      ) : files.length > 0 ? (
        <List dense disablePadding>
          {files.map((f) => (
            <ListItem
              key={f.id}
              disableGutters
              secondaryAction={f.is_mine && (
                <IconButton edge="end" size="small" onClick={() => handleDelete(f.id)} aria-label="Remove file">
                  <DeleteOutline fontSize="small" />
                </IconButton>
              )}
            >
              <InsertDriveFile fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <ListItemText
                primary={(
                  <Link href={f.file_url} target="_blank" rel="noopener noreferrer" underline="hover">
                    {f.file_name}
                  </Link>
                )}
                secondary={`${f.is_mine ? 'You' : f.uploader_name} · ${new Date(f.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}${f.file_size ? ` · ${formatSize(f.file_size)}` : ''}`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ my: 0.5 }}>
          No files shared yet. Attach plans, photos, or documents for the other party.
        </Typography>
      )}
      <Button
        component="label"
        size="small"
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={14} /> : <AttachFile />}
        disabled={uploading}
        sx={{ mt: 0.5 }}
      >
        {uploading ? 'Uploading…' : 'Attach Files'}
        <input type="file" hidden multiple onChange={handleUpload} accept="image/*,video/*,.pdf,.doc,.docx,.skp,.layout,.dwg,.dxf,.rvt,.rfa,.pln,.3ds,.max,.obj,.stl,.fbx,.dae,.blend,.glb,.gltf,.ifc" />
      </Button>
    </Box>
  );
};

export default QuoteAttachments;
